import { motion } from "framer-motion";
import { useLocale } from "../i18n/LocaleContext";

export function IntelligenceNode() {
  const { copy, locale } = useLocale();
  const { intelligence } = copy;
  const isChinese = locale === "zh" || locale === "vi";
  const [leadNode, ...supportNodes] = intelligence.nodes;
  const visuals = [
    "/brand-showcase/hero-products-source.jpg",
    "/official-brands/ilso-share.jpg",
    "/brand-showcase/showcase-wide.jpg",
  ];

  return (
    <section
      id="ai-solutions"
      className="bg-[linear-gradient(180deg,rgba(239,246,250,0.5),rgba(230,239,245,0.72))] px-5 py-24 sm:px-8 lg:px-12"
    >
      <div className="mx-auto max-w-7xl">
        <div className="grid gap-6 lg:grid-cols-[0.86fr_1.14fr] lg:items-end">
          <div className="max-w-[28rem]">
            <p className="text-sm uppercase tracking-[0.4em] text-aurora/85">{intelligence.eyebrow}</p>
            <h2
              className={`mt-4 text-[#243238] ${
                isChinese
                  ? "font-sans text-[2.15rem] font-medium tracking-[-0.01em] sm:text-[3rem]"
                  : "font-display text-4xl font-semibold sm:text-5xl"
              }`}
            >
              {intelligence.title}
            </h2>
          </div>
          <p className="max-w-[20rem] text-[0.9rem] leading-7 text-soft/58 lg:justify-self-end">
            {intelligence.body}
          </p>
        </div>

        <div className="mt-12 grid gap-5 lg:grid-cols-[1.04fr_0.96fr]">
          <motion.article
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="rounded-[2.5rem] border border-[#dce8ee] bg-[linear-gradient(180deg,rgba(255,255,255,0.9),rgba(240,246,249,0.96))] p-7 shadow-[0_14px_28px_rgba(106,138,151,0.06)] backdrop-blur sm:p-8"
          >
            <div className="mb-7 overflow-hidden rounded-[1.9rem] bg-[linear-gradient(180deg,rgba(255,255,255,0.98),rgba(242,248,251,0.94))]">
              <img src={visuals[0]} alt={leadNode.title} className="h-56 w-full object-cover object-center" />
            </div>
            <div className="flex items-center justify-between gap-4">
              <div className="flex h-14 w-14 items-center justify-center rounded-[1.4rem] bg-[#e9f3f7] text-sm font-semibold tracking-[0.16em] text-[#6a8793] shadow-[inset_0_0_0_1px_rgba(122,164,178,0.18)]">
                01
              </div>
              <div className="rounded-full bg-[#eef7fa] px-3 py-1 text-[10px] uppercase tracking-[0.22em] text-soft/58">
                {leadNode.stat}
              </div>
            </div>
            <h3 className="mt-7 max-w-[18rem] font-display text-[1.95rem] leading-tight text-[#243238] sm:text-[2.2rem]">
              {leadNode.title}
            </h3>
            <p className="mt-5 max-w-[18rem] text-[0.9rem] leading-7 text-soft/58">{leadNode.body}</p>
            <div className="mt-6 flex flex-wrap gap-2.5">
              {leadNode.tags.map((tag) => (
                <span
                  key={tag}
                  className="rounded-full bg-[rgba(244,251,253,0.92)] px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] text-soft/58"
                >
                  {tag}
                </span>
              ))}
            </div>
          </motion.article>

          <div className="grid gap-5">
            {supportNodes.map((item, index) => (
            <motion.article
              key={item.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.08 }}
            className="rounded-[2.2rem] border border-[#dce8ee] bg-[linear-gradient(180deg,rgba(255,255,255,0.88),rgba(241,247,250,0.96))] p-6 shadow-[0_12px_24px_rgba(106,138,151,0.06)] backdrop-blur"
            >
              <div className="mb-5 overflow-hidden rounded-[1.5rem] bg-[linear-gradient(180deg,rgba(255,255,255,0.98),rgba(242,248,251,0.94))]">
                <img
                  src={visuals[index + 1] ?? visuals[0]}
                  alt={item.title}
                  className="h-36 w-full object-cover object-center"
                />
              </div>
              <div className="mb-5 flex items-center justify-between gap-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#e9f3f7] text-sm font-semibold text-[#6a8793] shadow-[inset_0_0_0_1px_rgba(122,164,178,0.18)]">
                  0{index + 2}
                </div>
                <div className="rounded-full bg-[#eef7fa] px-3 py-1 text-[10px] uppercase tracking-[0.22em] text-soft/58">
                  {item.stat}
                </div>
              </div>
              <h3 className="font-display text-[1.55rem] leading-tight text-[#243238]">{item.title}</h3>
              <p className="mt-4 max-w-[15rem] text-[0.9rem] leading-7 text-soft/58">{item.body}</p>
              <div className="mt-5 flex flex-wrap gap-2">
                {item.tags.map((tag) => (
                  <span
                    key={tag}
                    className="rounded-full bg-[rgba(244,251,253,0.92)] px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] text-soft/56"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </motion.article>
            ))}
          </div>
        </div>

        <div className="mt-8 grid gap-5 lg:grid-cols-[0.88fr_1.12fr]">
          <motion.article
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55 }}
            className="rounded-[2.1rem] border border-[#dce8ee] bg-[linear-gradient(180deg,rgba(255,255,255,0.88),rgba(241,247,250,0.96))] p-6 shadow-[0_12px_24px_rgba(106,138,151,0.06)] backdrop-blur sm:p-7"
          >
            <div className="text-[10px] uppercase tracking-[0.28em] text-aurora/74">
              {intelligence.contextEyebrow}
            </div>
            <h3
              className={`mt-4 text-[#243238] ${
                isChinese
                  ? "font-sans text-[1.65rem] font-medium tracking-[-0.01em] sm:text-[2rem]"
                  : "font-display text-[1.95rem]"
              }`}
            >
              {intelligence.contextTitle}
            </h3>
            <div className="mt-5 space-y-4">
              {intelligence.contextParagraphs.slice(0, 2).map((paragraph) => (
                <p key={paragraph} className="max-w-[21rem] text-[0.9rem] leading-7 text-soft/58">
                  {paragraph}
                </p>
              ))}
            </div>
            <div className="mt-7 grid gap-3 sm:grid-cols-2">
              <div className="rounded-[1.4rem] bg-[rgba(246,251,253,0.94)] px-4 py-4">
                <div className="text-[10px] uppercase tracking-[0.22em] text-soft/50">{intelligence.workflowLabel}</div>
                <div className="mt-2 text-sm text-[#243238]">{intelligence.workflowValue}</div>
              </div>
              <div className="rounded-[1.4rem] bg-[rgba(246,251,253,0.94)] px-4 py-4">
                <div className="text-[10px] uppercase tracking-[0.22em] text-soft/50">{intelligence.focusLabel}</div>
                <div className="mt-2 text-sm text-[#243238]">{intelligence.focusValue}</div>
              </div>
            </div>
          </motion.article>

          <motion.article
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55, delay: 0.05 }}
            className="overflow-hidden rounded-[2rem] border border-[#d8e6ed] bg-[linear-gradient(180deg,rgba(233,242,246,0.96),rgba(218,231,238,0.98))] shadow-[0_24px_44px_rgba(106,138,151,0.12)]"
          >
            <div className="flex items-center justify-between border-b border-[#d4e3ea] px-5 py-4 sm:px-6">
              <div className="flex items-center gap-2.5">
                <span className="h-2.5 w-2.5 rounded-full bg-[#ff8b7b]" />
                <span className="h-2.5 w-2.5 rounded-full bg-[#ffd166]" />
                <span className="h-2.5 w-2.5 rounded-full bg-[#7fd1b9]" />
              </div>
              <div className="rounded-full bg-white/55 px-3 py-1 text-[10px] uppercase tracking-[0.24em] text-[#4f6570]">
                {intelligence.terminalLive}
              </div>
            </div>
            <div className="space-y-3 px-5 py-5 sm:px-6 sm:py-6">
              {intelligence.terminalLines.map((line, index) => {
                const toneClass =
                  line.tone === "DONE"
                    ? "text-[#5d9d79]"
                    : line.tone === "INFO"
                      ? "text-[#6c94ac]"
                      : "text-[#60747f]";

                return (
                  <div
                    key={`${line.text}-${index}`}
                    className="grid grid-cols-[auto_1fr] gap-3 rounded-[1rem] bg-white/52 px-4 py-3"
                  >
                    <div className={`text-[11px] uppercase tracking-[0.2em] ${toneClass}`}>{line.tone}</div>
                    <div className="text-[0.92rem] leading-7 text-[#38505b]">{line.text}</div>
                  </div>
                );
              })}
            </div>
          </motion.article>
        </div>
      </div>
    </section>
  );
}
